import { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { AnnouncementsQuery } from 'interfaceAdapter/queries/Announcements';
import { IAnnouncement } from 'domain/entities/Announcement/Announcement';

export const useAnnouncements = () => {
    const diContainer = useContext(diContainerContext);
    const [announcements, setAnnouncements] = useState<IAnnouncement[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const queryRef = useRef(diContainer.resolve(AnnouncementsQuery));

    const fetchAnnouncements = useCallback(async (offset: number = 0) => {
        try {
            setIsLoading(true);
            const result = await queryRef.current.fetch({
                limit: 6, // 6件取得して、5件表示、次があるかチェック
                offset,
                targetType: 'buyer',
            });

            if (result) {
                const sliced = result.slice(0, 5);
                if (offset === 0) {
                    // 初期読み込み
                    setAnnouncements(sliced);
                } else {
                    // 追加読み込み
                    setAnnouncements((prev) => [...prev, ...sliced]);
                }
                // 追加取得時は5件未満ならhasMoreをfalseに
                if (offset > 0 && sliced.length < 5) {
                    setHasMore(false);
                } else {
                    setHasMore(result.length > 5 && sliced.length === 5);
                }
            } else {
                setAnnouncements([]);
                setHasMore(false);
            }
        } catch (error) {
            console.error('Failed to fetch announcements:', error);
            setAnnouncements([]);
            setHasMore(false);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const loadMore = useCallback(() => {
        if (!isLoading && hasMore) {
            fetchAnnouncements(announcements.length);
        }
    }, [fetchAnnouncements, announcements.length, isLoading, hasMore]);

    useEffect(() => {
        fetchAnnouncements();
    }, [fetchAnnouncements]);

    return {
        announcements,
        hasMore,
        isLoading,
        loadMore,
    };
};
